import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Video,
  Camera,
  Activity,
  Flame,
  Link2,
  Tag,
  RefreshCw,
  ArrowRight,
  LayoutGrid,
} from "lucide-react";
import { toast } from "react-toastify";
import { SpinLoader } from "../../../components/loader/Loader";
import { getSurveillanceOverview } from "../../../service/securityService";
import type { SurveillanceOverview, MotionAlert } from "../../../types/security_types";

const SurveillanceHub: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<SurveillanceOverview | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const o = await getSurveillanceOverview();
      setData(o);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load surveillance overview");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const cards = [
    {
      title: "Camera master",
      description: "Register cameras, stream URLs, zones, and maintenance status.",
      path: "/security/surveillance/cameras",
      icon: Camera,
    },
    {
      title: "Live view & playback",
      description: "Open live streams and recorded playback when URLs are configured.",
      path: "/security/surveillance/live",
      icon: Video,
    },
    {
      title: "Camera health",
      description: "Run health checks and review last check timestamps.",
      path: "/security/surveillance/health",
      icon: Activity,
    },
    {
      title: "Motion alerts",
      description: "Review and acknowledge motion events from analytics or VMS webhooks.",
      path: "/security/surveillance/motion-alerts",
      icon: Flame,
    },
    {
      title: "Crowd heatmap",
      description: "Zone density proxy from access logs (or camera coverage fallback).",
      path: "/security/surveillance/heatmap",
      icon: LayoutGrid,
    },
    {
      title: "VMS integration",
      description: "Capabilities and patterns for connecting external video systems.",
      path: "/security/surveillance/vms",
      icon: Link2,
    },
    {
      title: "Incident video tagging",
      description: "Attach clip metadata and URLs to security incidents.",
      path: "/security/surveillance/incident-clips",
      icon: Tag,
    },
  ];

  return (
    <div className="px-2 py-4 w-full h-full mt-10 space-y-6">
      <div className="flex flex-wrap justify-between items-start gap-4">
        <div className="flex items-center gap-3">
          <div className="bg-[#EAFBFA] p-3 rounded-lg">
            <Video className="w-7 h-7 text-[#0fa9a6]" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Surveillance &amp; monitoring</h1>
            <p className="text-gray-600 text-sm max-w-3xl">
              CCTV master data, live and playback URLs, health checks, motion alerts, crowd-density
              views, VMS integration, and incident clip linking share the same camera registry and
              security APIs.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => load()}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-full text-sm hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {loading && !data ? (
        <div className="flex justify-center py-20">
          <SpinLoader />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <p className="text-xs text-gray-500">Cameras</p>
              <p className="text-2xl font-bold text-gray-900">{data?.cameras.total ?? "—"}</p>
              <p className="text-xs text-gray-400 mt-1">{data?.cameras.active ?? 0} active</p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <p className="text-xs text-gray-500">Maintenance</p>
              <p className="text-2xl font-bold text-amber-700">
                {data?.cameras.maintenance ?? "—"}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <p className="text-xs text-gray-500">Health (online)</p>
              <p className="text-2xl font-bold text-emerald-700">
                {data?.cameras.onlineHealth ?? "—"}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-100">
              <p className="text-xs text-gray-500">Motion (unacked)</p>
              <p className="text-2xl font-bold text-red-600">
                {data?.motion.unacknowledged ?? "—"}
              </p>
              <p className="text-xs text-gray-400 mt-1">{data?.motion.last24h ?? 0} in 24h</p>
            </div>
          </div>

          {data && data.recentMotionAlerts.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-100 p-4 shadow-sm">
              <h2 className="text-sm font-semibold text-gray-800 mb-2">Recent motion alerts</h2>
              <ul className="text-sm text-gray-600 space-y-1">
                {data.recentMotionAlerts.map((a: MotionAlert) => (
                  <li key={a.id || a._id}>
                    <span className="font-medium text-gray-800">{a.cameraId}</span>
                    {a.cameraName ? ` · ${a.cameraName}` : ""} — {a.message}{" "}
                    <span className="text-gray-400">
                      ({a.acknowledged ? "acked" : "open"})
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {cards.map((c) => {
              const Icon = c.icon;
              return (
                <button
                  key={c.path}
                  type="button"
                  onClick={() => navigate(c.path)}
                  className="text-left bg-white rounded-xl border border-gray-100 shadow-sm p-5 hover:border-[#0fa9a6]/40 hover:shadow-md transition-all group"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="bg-[#EAFBFA] p-2 rounded-lg">
                        <Icon className="w-5 h-5 text-[#0fa9a6]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{c.title}</h3>
                        <p className="text-sm text-gray-600 mt-1">{c.description}</p>
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-[#0fa9a6] shrink-0 mt-1" />
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default SurveillanceHub;
